/**
 * Client-side route table (N9) — code-split via React.lazy.
 *
 * Each page becomes its own JS chunk the browser fetches on demand.
 * The initial visit to `/produtos/fixadores/.../orlock` downloads only
 * the Orlock page + shared vendor code; visiting `/sobre` later fetches
 * the About chunk separately.
 *
 * SSR is unaffected: entry-server.tsx uses routes-server.tsx (eager
 * imports) so prerendered HTML is never dependent on an async boundary.
 *
 * The Suspense fallback shows on FIRST page load, not on client-side
 * navigation. main.tsx mounts with createRoot, which replaces the
 * prerendered DOM, and nothing loads the route's module before that
 * first render: lazyWithReload only renders synchronously once its module
 * has resolved, and the prerendered HTML's modulepreload only starts the
 * fetch early. So React's first paint is PageFallback inside the site
 * shell. Navigations don't show it: React Router 7 runs each one in
 * startTransition, so React keeps the current page on screen until the
 * next page's chunk lands.
 *
 * To preload chunks on hover, see links that use the pre-fetch pattern
 * (future enhancement — not in this first cut).
 */

import { Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { lazyWithReload } from "@/lib/lazy-with-reload";

/**
 * Legacy /produtos/servicos/<slug> → /servicos/<slug> redirect.
 * Preserves any URLs an editor or external link still points at the
 * old structure. Slug param name was `familySlug` historically; we
 * accept anything and forward.
 */
const RedirectToServices = () => {
  const { slug } = useParams();
  return <Navigate to={`/servicos/${slug ?? ""}`} replace />;
};

// `lazyWithReload` wraps `React.lazy` so a stale chunk after a deploy
// triggers a one-shot full reload instead of throwing the
// "'text/html' is not a valid JavaScript MIME type" error a user
// hit on /servicos/assistencia-tecnica (Sentry ORBRASIL-FRONTEND-2).
// See src/lib/lazy-with-reload.ts for the rationale.
const Index = lazyWithReload(() => import("./pages/Index"));
const About = lazyWithReload(() => import("./pages/About"));
const Events = lazyWithReload(() => import("./pages/Events"));
const EventDetail = lazyWithReload(() => import("./pages/EventDetail"));
const Campaign = lazyWithReload(() => import("./pages/Campaign"));
const Contact = lazyWithReload(() => import("./pages/Contact"));
// CMS Live Preview target (loaded inside the admin edit-view iframe).
const Preview = lazyWithReload(() => import("./pages/Preview"));
const ProductLine = lazyWithReload(() => import("./pages/ProductLine"));
const ProductFamily = lazyWithReload(() => import("./pages/ProductFamily"));
const ProductGroup = lazyWithReload(() => import("./pages/ProductGroup"));
const ProductDetail = lazyWithReload(() => import("./pages/ProductDetail"));
const ServicePage = lazyWithReload(() => import("./pages/ServicePage"));
const Services = lazyWithReload(() => import("./pages/Services"));
const Compare = lazyWithReload(() => import("./pages/Compare"));
const Search = lazyWithReload(() => import("./pages/Search"));
// /social is the QR-code linktree. It renders bare (no Layout), is
// noindex, and nothing on the site links to it — see pages/Social.tsx.
const Social = lazyWithReload(() => import("./pages/Social"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));

// `routePages` was exported here to let main.tsx preload the matched
// route's chunk before hydrateRoot fired (PR #44 ship/hydrate-root).
// hydrateRoot was rejected on Lighthouse data (PERF-NIGHT wave 12) and
// production stays on createRoot, so this export had no remaining
// callers. Removed 2026-05-10 in the dead-code sweep. If a future
// hydrateRoot reconsideration ever lands, restore from git history —
// the route component instances above are unchanged, so the preload
// pattern would still be one re-export away.

/** Spinner shown in place of a page while its chunk loads. */
const PageFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" aria-label="Carregando" />
  </div>
);

/**
 * The site shell (Header, <main>, Footer) for every route except /social
 * and /preview. It sits ABOVE the Suspense boundary, so only the page
 * inside <main> waits for its chunk, and the Header is mounted once for
 * the whole visit. Pages don't wrap themselves in <Layout>.
 *
 * It used to be the other way round: every page wrapped itself in
 * <Layout>, and so did PageFallback. On first load the fallback's Header
 * mounted first and the page's Header replaced it when the chunk landed,
 * throwing away whatever the visitor had done in it: an open search, the
 * text typed into it, the focus. tests/e2e/header-first-load.spec.ts
 * guards this.
 */
const SiteShell = () => (
  <Layout>
    <Suspense fallback={<PageFallback />}>
      <Outlet />
    </Suspense>
  </Layout>
);

export const AppRoutes = () => (
  // Only /social and /preview suspend at this outer boundary; everything
  // else suspends inside SiteShell's. Neither has the shell, so the spinner
  // stands alone here.
  <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route element={<SiteShell />}>
        <Route path="/" element={<Index />} />
        <Route path="/sobre" element={<About />} />
        <Route path="/eventos" element={<Events />} />
        <Route path="/eventos/:eventSlug" element={<EventDetail />} />
        <Route path="/campanhas/:slug" element={<Campaign />} />
        <Route path="/contato" element={<Contact />} />
        {/* Services live under /servicos/<slug> — they're a different
            content shape from the catalog (no SKU, no specs, no
            variants). /servicos (no slug) is the index page that lists
            all services. The legacy /produtos/servicos/* routes 301-
            redirect so external links + bookmarks keep working. */}
        <Route path="/servicos" element={<Services />} />
        <Route path="/servicos/:slug" element={<ServicePage />} />
        <Route
          path="/produtos/servicos"
          element={<Navigate to="/" replace />}
        />
        <Route
          path="/produtos/servicos/:slug"
          element={<RedirectToServices />}
        />
        <Route path="/produtos" element={<Navigate to="/" replace />} />
        <Route path="/produtos/:lineSlug" element={<ProductLine />} />
        <Route path="/produtos/:lineSlug/:familySlug" element={<ProductFamily />} />
        <Route path="/produtos/:lineSlug/:familySlug/:groupSlug" element={<ProductGroup />} />
        <Route path="/produtos/:lineSlug/:familySlug/:groupSlug/:productSku" element={<ProductDetail />} />
        <Route path="/busca" element={<Search />} />
        <Route path="/comparador" element={<Compare />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* QR-code landing page. Intentionally unlinked from the site, and
          outside the shell: it has no Header or Footer (see Social.tsx). */}
      <Route path="/social" element={<Social />} />
      {/* CMS Live Preview — rendered inside the admin edit-view iframe.
          Client-only (not prerendered); the worker allows framing here
          only from the admin origin. Outside the shell because its
          placeholder states are bare; it wraps the product page in its
          own Layout. */}
      <Route path="/preview/:collection" element={<Preview />} />
    </Routes>
  </Suspense>
);
